import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Filter } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs", {
        params: {
          actor: actor || undefined,
          action: action || undefined,
          entity_type: entity || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      });
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          Super Admin
        </p>
        <h1 className="text-4xl font-serif text-forest-900 mt-1">Audit Log</h1>
        <p className="mt-2 text-sm text-charcoal-soft max-w-2xl">
          Every meaningful action is recorded here. This log is immutable — records can
          never be edited or deleted.
        </p>
      </div>

      <div className="patria-card p-4 grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            Actor
          </label>
          <input
            className="patria-input w-full"
            placeholder="Name or email"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            data-testid="audit-actor"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            Action
          </label>
          <input
            className="patria-input w-full"
            placeholder="e.g., login"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            data-testid="audit-action"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            Entity type
          </label>
          <input
            className="patria-input w-full"
            placeholder="e.g., user"
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            data-testid="audit-entity"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            From
          </label>
          <input
            type="date"
            className="patria-input w-full"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="audit-from"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            To
          </label>
          <input
            type="date"
            className="patria-input w-full"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="audit-to"
          />
        </div>
        <button onClick={load} className="patria-btn-primary sm:col-span-6" data-testid="audit-apply">
          <Filter className="h-4 w-4" strokeWidth={1.5} /> Apply filters
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl shadow-card">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th>When</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Entity</th>
              <th>Details</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="italic text-charcoal-muted">
                  Loading audit records...
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="italic text-charcoal-muted">
                  No records match those filters.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const isBreakGlass = r.action === "BREAK_GLASS_DEANONYMIZE";
              return (
              <tr key={r.id} data-testid={`audit-row-${r.id}`} className={isBreakGlass ? "bg-terracotta-500/10" : ""}>
                <td className="whitespace-nowrap text-xs">{formatTs(r.timestamp)}</td>
                <td>
                  <p className="font-medium text-forest-900">{r.actor_name}</p>
                  {r.actor_email && (
                    <p className="text-xs text-charcoal-muted">{r.actor_email}</p>
                  )}
                </td>
                <td>
                  <span className={isBreakGlass ? "patria-chip-terra font-bold uppercase" : "patria-chip-green"}>
                    {isBreakGlass ? "🔒 Break-glass" : r.action}
                  </span>
                </td>
                <td>
                  <span className="patria-chip-terra">{r.entity_type}</span>
                  {r.entity_id && (
                    <p className="text-xs text-charcoal-muted mt-0.5 truncate max-w-[220px]">
                      {r.entity_id}
                    </p>
                  )}
                </td>
                <td className="text-xs">
                  <code className="text-charcoal-soft break-all">
                    {r.details && Object.keys(r.details).length
                      ? JSON.stringify(r.details)
                      : "—"}
                  </code>
                </td>
                <td className="text-xs text-charcoal-muted">{r.ip_address || "—"}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatTs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
